/**
 * Consistent calendar-date display for brand timezone + locale (used by DatePicker input,
 * gallery summaries, tests). Does not parse ISO strings — pass a `Date` from your pipeline
 * (e.g. `convertIsoToDateObject` in gallery utils).
 */

export type BrandCalendarDatePreset = 'numeric' | 'medium';

const PRESET_PARTS: Record<
    BrandCalendarDatePreset,
    Pick<Intl.DateTimeFormatOptions, 'year' | 'month' | 'day'>
> = {
    /** e.g. 04/28/2026 — matches read-only date picker input */
    numeric: { year: 'numeric', month: '2-digit', day: '2-digit' },
    /** e.g. 28 Apr 2026 — shorter labels in sidebars / lists */
    medium: { year: 'numeric', month: 'short', day: 'numeric' }
};

export interface IFormatBrandCalendarDateOptions {
    /** Brand locale tag (`en_GB` → normalize before calling, or pass raw and we normalize). */
    brandLocale?: string | null;
    /**
     * When `brandLocale` is missing (e.g. optional brand). In browser code, pass
     * `typeof navigator !== 'undefined' ? navigator.language : undefined`.
     */
    fallbackLocale?: string;
    /** IANA zone from brand; when set, calendar fields are evaluated in this zone. */
    timeZone?: string | null;
    preset?: BrandCalendarDatePreset;
}

export function normalizeBrandLocaleTag(raw: string | null | undefined): string | undefined {
    if (typeof raw !== 'string') {
        return undefined;
    }

    const t = raw.trim();

    if (!t) {
        return undefined;
    }

    return t.replace(/_/g, '-');
}

/**
 * Formats a `Date` for display using brand locale (with optional browser fallback) and
 * optional brand `timeZone`, matching `DatePickerInput` when `preset === 'numeric'`.
 */
export function formatBrandCalendarDate(
    date: Date,
    options: IFormatBrandCalendarDateOptions = {}
): string {
    const { brandLocale, fallbackLocale, timeZone, preset = 'numeric' } = options;
    const locale = normalizeBrandLocaleTag(brandLocale) ?? fallbackLocale;
    const tz = typeof timeZone === 'string' && timeZone.trim() ? timeZone.trim() : undefined;

    return new Intl.DateTimeFormat(locale, {
        ...PRESET_PARTS[preset],
        ...(tz ? { timeZone: tz } : {})
    }).format(date);
}

/**
 * Calendar date as `YYYY-MM-DD` in the given IANA zone for this instant — must match gallery
 * `convertIsoDateToInputFormat` (sv-SE + timeZone). Uses `formatToParts` so output is always
 * canonical `YYYY-MM-DD` with padded segments (`.format()` alone can omit leading zeros in some runtimes).
 */
export function formatCalendarYmdInTimeZone(date: Date, timeZone: string): string {
    const tz = timeZone.trim();

    if (!tz) {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    const parts = new Intl.DateTimeFormat('sv-SE', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(date);

    const y = parts.find((p) => p.type === 'year')?.value;
    const mo = parts.find((p) => p.type === 'month')?.value;
    const d = parts.find((p) => p.type === 'day')?.value;

    if (y && mo && d) {
        return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }

    const fallback = new Intl.DateTimeFormat('sv-SE', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(date);

    return /^\d{4}-\d{1,2}-\d{1,2}$/.test(fallback)
        ? (() => {
              const [yy, mm, dd] = fallback.split('-');
              return `${yy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
          })()
        : fallback;
}

/**
 * Parse canonical `YYYY-MM-DD` as **local** calendar midnight — same wall‑date semantics as
 * gallery `parseDateString` / DatePicker `selected` normalization from brand-zone calendar strings.
 */
export function parseCalendarYmdAsLocalDate(ymd: string): Date | null {
    const m = ymd.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!m) {
        return null;
    }

    const year = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    const day = parseInt(m[3], 10);

    if ([year, month, day].some((n) => Number.isNaN(n))) {
        return null;
    }

    return new Date(year, month - 1, day);
}
